import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
    HiOutlineArrowLeft,
    HiOutlinePencil,
    HiOutlinePlay,
    HiOutlinePause,
    HiOutlineStop,
    HiOutlineChartBar,
    HiOutlineDocumentText,
    HiOutlineEye,
    HiOutlineLink,
    HiOutlineClipboardCopy,
    HiOutlineExternalLink
} from "react-icons/hi";
import { HiCheck } from "react-icons/hi2";
import { updateSurveyStatus } from "../../redux/actions/surveyActions";
import { getSurveyAnalytics, getSurveyResponses } from "../../redux/api/surveyAPI";
import { useDispatch } from "react-redux";
import { Snackbar } from "../shared/Snackbar";
import SurveyAnalytics from "./SurveyAnalytics";
import SurveyResponses from "./SurveyResponses";
import Button from "../shared/Button";
import Card from "../shared/Card";
import Badge from "../shared/Badge";
import TextInput from "../shared/TextInput";
import { cn } from "../../utils/cn";

const SingleSurvey = ({ survey, onClose, onEdit, isAdmin = true }) => {
    const dispatch = useDispatch();
    const token = useSelector((state) => state.auth?.accessToken);
    const currentOrganisation = useSelector((state) => state.organisation?.currentOrganisation);
    const [activeTab, setActiveTab] = useState("analytics");
    const [analytics, setAnalytics] = useState(null);
    const [responses, setResponses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ show: false, message: "", type: "success" });
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);

    const getSurveyLink = () => {
        const customDomain = currentOrganisation?.settings?.shop?.customShopDomain;
        const isVerified = currentOrganisation?.settings?.shop?.isVerified;
        if (isVerified && customDomain) {
            const domain = customDomain.startsWith("https://") ? customDomain : `https://${customDomain}`;
            return `${domain}/survey/${survey._id}`;
        }
        return `${window.location.origin}/survey/${survey._id}`;
    };

    const handleCopyLink = () => {
        navigator.clipboard
            .writeText(getSurveyLink())
            .then(() => {
                setLinkCopied(true);
                setTimeout(() => setLinkCopied(false), 2000);
                showSnackbar("Survey link copied to clipboard!");
            })
            .catch(() => {
                showSnackbar("Failed to copy link", "error");
            });
    };

    const showSnackbar = (message, type = "success") => {
        setSnackbar({ show: true, message, type });
        setTimeout(() => setSnackbar({ show: false, message: "", type: "success" }), 3000);
    };

    useEffect(() => {
        if (activeTab === "analytics") {
            loadAnalytics();
        } else if (activeTab === "responses") {
            loadResponses();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, survey._id]);

    const loadAnalytics = async () => {
        setLoading(true);
        try {
            const result = await getSurveyAnalytics(survey._id, token);
            setAnalytics(result.analytics);
        } catch (error) {
            showSnackbar("Failed to load analytics", "error");
        } finally {
            setLoading(false);
        }
    };

    const loadResponses = async () => {
        setLoading(true);
        try {
            const result = await getSurveyResponses(survey._id, token);
            setResponses(result.responses);
        } catch (error) {
            showSnackbar("Failed to load responses", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (newStatus) => {
        const result = await dispatch(updateSurveyStatus(survey._id, newStatus));
        if (result.success) {
            showSnackbar(
                `Survey ${newStatus === "active" ? "activated" : newStatus === "paused" ? "paused" : "ended"} successfully`
            );
            survey.status = newStatus;
        } else {
            showSnackbar(result.error || "Failed to update survey status", "error");
        }
    };

    const getStatusVariant = (status) => {
        switch (status) {
            case "active":
                return "success";
            case "paused":
                return "warning";
            case "ended":
                return "coral";
            case "draft":
            default:
                return "default";
        }
    };

    const formatDate = (date) => {
        if (!date) return "N/A";
        return new Date(date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const tabClass = (tab) =>
        cn(
            "flex items-center gap-2 px-6 py-3 font-medium transition-colors border-b-2",
            activeTab === tab
                ? "text-ink border-ink"
                : "text-muted border-transparent hover:text-ink"
        );

    return (
        <div className="p-6 bg-canvas min-h-screen">
            {/* Header */}
            <div className="mb-6">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    leadingIcon={<HiOutlineArrowLeft className="text-xl" />}
                    className="mb-4"
                >
                    Back to Surveys
                </Button>

                <div className="flex justify-between items-start gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h1 className="text-display-md text-ink">{survey.title}</h1>
                            <Badge variant={getStatusVariant(survey.status)}>
                                {survey.status.charAt(0).toUpperCase() + survey.status.slice(1)}
                            </Badge>
                        </div>
                        {survey.description && (
                            <p className="text-title-md text-body mb-4">{survey.description}</p>
                        )}
                        <div className="flex gap-4 text-body-md text-muted flex-wrap">
                            <div>Created: {formatDate(survey.createdAt)}</div>
                            {survey.startDate && <div>Started: {formatDate(survey.startDate)}</div>}
                            {survey.endDate && <div>Ended: {formatDate(survey.endDate)}</div>}
                        </div>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                        {/* Get Link Button */}
                        <div className="relative">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setShowLinkModal(!showLinkModal)}
                                leadingIcon={<HiOutlineLink />}
                            >
                                Get Link
                            </Button>

                            {showLinkModal && (
                                <div className="absolute right-0 top-12 z-50 p-4 rounded-lg shadow-card border border-hairline bg-canvas min-w-[400px]">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="text-title-sm text-ink">Survey Link</h4>
                                        <button
                                            onClick={() => setShowLinkModal(false)}
                                            className="text-xl text-muted hover:text-ink"
                                        >
                                            ×
                                        </button>
                                    </div>

                                    {!currentOrganisation?.settings?.shop?.isVerified && (
                                        <div className="mb-3 p-3 rounded-md bg-signature-cream border border-signature-mustard">
                                            <p className="text-body-md text-ink">
                                                Your domain is not verified. The link uses the default application URL.{" "}
                                                <a href="/settings" className="underline ml-1 font-medium text-link">
                                                    Verify your domain in Settings
                                                </a>
                                            </p>
                                        </div>
                                    )}

                                    <div className="flex items-center gap-2">
                                        <TextInput
                                            type="text"
                                            readOnly
                                            value={getSurveyLink()}
                                            inputClassName="flex-1"
                                            className="font-mono"
                                        />
                                        <Button
                                            variant={linkCopied ? "primary" : "primary"}
                                            size="sm"
                                            onClick={handleCopyLink}
                                            leadingIcon={linkCopied ? <HiCheck /> : <HiOutlineClipboardCopy />}
                                        >
                                            {linkCopied ? "Copied" : "Copy"}
                                        </Button>
                                    </div>

                                    <div className="mt-3 flex justify-end">
                                        <a
                                            href={getSurveyLink()}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 text-body-md font-medium text-link hover:underline"
                                        >
                                            <HiOutlineExternalLink />
                                            Open in new tab
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>

                        {isAdmin && (
                        <>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => window.open(`${getSurveyLink()}?preview=1`, "_blank", "noopener")}
                            leadingIcon={<HiOutlineEye />}
                        >
                            Preview
                        </Button>

                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={onEdit}
                            leadingIcon={<HiOutlinePencil />}
                        >
                            Edit
                        </Button>

                        {survey.status === "draft" && (
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleStatusChange("active")}
                                leadingIcon={<HiOutlinePlay />}
                            >
                                Activate
                            </Button>
                        )}

                        {survey.status === "active" && (
                            <>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handleStatusChange("paused")}
                                    leadingIcon={<HiOutlinePause />}
                                >
                                    Pause
                                </Button>
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleStatusChange("ended")}
                                    leadingIcon={<HiOutlineStop />}
                                >
                                    End
                                </Button>
                            </>
                        )}

                        {survey.status === "paused" && (
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleStatusChange("active")}
                                leadingIcon={<HiOutlinePlay />}
                            >
                                Resume
                            </Button>
                        )}
                        </>
                        )}
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Card variant="surface-soft" className="!p-4">
                    <div className="text-body-md text-muted mb-1">Total Visits</div>
                    <div className="text-display-md text-ink font-medium">
                        {survey.analytics?.totalVisits || 0}
                    </div>
                </Card>
                <Card variant="surface-soft" className="!p-4">
                    <div className="text-body-md text-muted mb-1">Started</div>
                    <div className="text-display-md text-ink font-medium">
                        {survey.analytics?.totalStarted || 0}
                    </div>
                </Card>
                <Card variant="cream-callout" className="!p-4">
                    <div className="text-body-md text-ink mb-1">Completed</div>
                    <div className="text-display-md text-ink font-medium">
                        {survey.analytics?.totalCompleted || 0}
                    </div>
                </Card>
                <Card variant="surface-soft" className="!p-4">
                    <div className="text-body-md text-muted mb-1">Completion Rate</div>
                    <div className="text-display-md text-ink font-medium">
                        {survey.analytics?.totalStarted > 0
                            ? Math.round((survey.analytics.totalCompleted / survey.analytics.totalStarted) * 100)
                            : 0}
                        %
                    </div>
                </Card>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-hairline mb-6">
                <button onClick={() => setActiveTab("analytics")} className={tabClass("analytics")}>
                    <HiOutlineChartBar />
                    Analytics
                </button>
                <button onClick={() => setActiveTab("responses")} className={tabClass("responses")}>
                    <HiOutlineDocumentText />
                    Responses ({responses.length})
                </button>
                <button onClick={() => setActiveTab("preview")} className={tabClass("preview")}>
                    <HiOutlineEye />
                    Preview
                </button>
            </div>

            {/* Content */}
            <div>
                {loading ? (
                    <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-2 border-hairline border-t-ink" />
                    </div>
                ) : (
                    <>
                        {activeTab === "analytics" && analytics && (
                            <SurveyAnalytics analytics={analytics} survey={survey} />
                        )}
                        {activeTab === "responses" && (
                            <SurveyResponses responses={responses} survey={survey} />
                        )}
                        {activeTab === "preview" && (
                            <div className="max-w-2xl mx-auto">
                                <Card variant="default" className="!p-8">
                                    <h2 className="text-display-md text-ink mb-4">{survey.title}</h2>
                                    {survey.description && (
                                        <p className="text-body-md text-body mb-6">{survey.description}</p>
                                    )}

                                    <div className="space-y-6">
                                        {survey.questions.map((question, index) => (
                                            <div
                                                key={index}
                                                className="pb-6 border-b border-hairline last:border-b-0"
                                            >
                                                <div className="flex gap-2 mb-3">
                                                    <span className="font-medium text-ink">{index + 1}.</span>
                                                    <div className="flex-1">
                                                        <span className="text-ink">{question.questionText}</span>
                                                        {question.required && (
                                                            <span className="text-signature-coral ml-1">*</span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="ml-6">
                                                    {["single_choice", "multiple_choice"].includes(question.questionType) && (
                                                        <div className="space-y-2">
                                                            {question.options.map((option, oIndex) => (
                                                                <label
                                                                    key={oIndex}
                                                                    className="flex items-center gap-2 cursor-pointer"
                                                                >
                                                                    <input
                                                                        type={question.questionType === "single_choice" ? "radio" : "checkbox"}
                                                                        name={`question_${index}`}
                                                                        disabled
                                                                        className="rounded"
                                                                    />
                                                                    <span className="text-ink">{option.text}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {question.questionType === "text_short" && (
                                                        <TextInput
                                                            type="text"
                                                            placeholder={question.placeholder || "Your answer"}
                                                            disabled
                                                        />
                                                    )}

                                                    {question.questionType === "text_long" && (
                                                        <TextInput
                                                            multiline
                                                            rows={4}
                                                            placeholder={question.placeholder || "Your answer"}
                                                            disabled
                                                        />
                                                    )}

                                                    {question.questionType === "rating_scale" && (
                                                        <div className="flex gap-2">
                                                            {Array.from(
                                                                { length: question.ratingConfig.max - question.ratingConfig.min + 1 },
                                                                (_, i) => question.ratingConfig.min + i
                                                            ).map((rating) => (
                                                                <button
                                                                    key={rating}
                                                                    disabled
                                                                    className="w-12 h-12 rounded-md border border-hairline font-medium text-ink"
                                                                >
                                                                    {rating}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {question.questionType === "yes_no" && (
                                                        <div className="flex gap-4">
                                                            <label className="flex items-center gap-2 cursor-pointer">
                                                                <input type="radio" name={`question_${index}`} disabled />
                                                                <span className="text-ink">Yes</span>
                                                            </label>
                                                            <label className="flex items-center gap-2 cursor-pointer">
                                                                <input type="radio" name={`question_${index}`} disabled />
                                                                <span className="text-ink">No</span>
                                                            </label>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Card>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Snackbar */}
            {snackbar.show && (
                <Snackbar
                    text={snackbar.message}
                    successIcon={snackbar.type === "success"}
                    onClose={() => setSnackbar({ show: false, message: "", type: "success" })}
                />
            )}
        </div>
    );
};

export default SingleSurvey;
